import { memo, useCallback, useEffect, useRef, useState } from "react";
import { TenantEditProps } from "./types";
import { showNotification } from "services/api/snackbar";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialTenantCreateForm } from "services/api/tenants/consts";
import { TENANT_INFO_FORM_SCHEMA } from "./validation";
import { createTenantAPI, getTenantAPI, updateTenantAPI } from "services/api/tenants/api";
import { getApiErrorByCode } from "services/api/transformation";
import {
  Tenant,
  TenantCreateFormValues,
  TenantEditFormValues,
} from "services/api/tenants/types";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import { Loader } from "ui/components/Loader";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import TenantInfoStep from "./components/TenantInfoStep/TenantInfoStep";
import TenantOfficeStep from "./components/TenantOfficeStep/TenantOfficeStep";
import TenantUserStep from "./components/TenantUserStep/TenantUserStep";
import TenantSubscriptionStep from "./components/TenantSubscriptionStep/TenantSubscriptionStep";
import TenantSummaryStep from "./components/TenantSummaryStep/TenantSummaryStep";
import TenantStepStatusBar from "./components/TenantStepStatusBar/TenantStepStatusBar";
import { Subscription, SubscriptionStepFormValues } from "services/api/subscriptions/types";
import { initialSubscriptionStepForm } from "services/api/subscriptions/consts";
import {
  getTenantSubscriptionAPI,
  signTenantSubscriptionAPI,
} from "services/api/subscriptions/api";

const TenantEditCard: React.FC<TenantEditProps> = (props) => {
  const { tenantId, show, onSuccess, onClose, isFullScreen = true } = props;

  const isEdit = !!tenantId;

  const [formValues, setFormValues] = useState<TenantCreateFormValues>({
    ...initialTenantCreateForm,
    tenantTypeId: "C",
  });
  const [loadedTenant, setLoadedTenant] = useState<Tenant | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [stepCurrent, setStepCurrent] = useState(0);
  const [officeCount, setOfficeCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  /*
    Pretplata se drži u stanju čarobnjaka i šalje tek na kraju, u jednom pozivu zajedno s
    pregazivanjima — inače bi klijent nakratko ostao s pretplatom bez odobrenih odstupanja.
  */
  const [subscriptionValues, setSubscriptionValues] = useState<SubscriptionStepFormValues>(
    initialSubscriptionStepForm
  );
  /** Popunjeno kad klijent već ima pretplatu — tada se nova ne potpisuje. */
  const [existingSubscription, setExistingSubscription] = useState<Subscription | null>(null);
  const showIntro = isFullScreen;
  const title = isEdit ? "Ažuriranje tenanta" : "Unos tenanta";
  const isProcessingRef = useRef(false);

  const handleOfficeCountChange = useCallback((count: number) => {
    setOfficeCount(count);
  }, []);

  const handleUserCountChange = useCallback((count: number) => {
    setUserCount(count);
  }, []);

  useEffect(() => {
    if (!show) return;

    setStepCurrent(0);
    setErrors({});
    setSubscriptionValues(initialSubscriptionStepForm);
    setExistingSubscription(null);

    if (tenantId) {
      fetchTenant(tenantId);
    } else {
      setLoadedTenant(null);
      setFormValues({
        ...initialTenantCreateForm,
        tenantTypeId: "C",
      });
    }
  }, [show, tenantId]);

  /*
    Osoba nema pretplatu, pa se prelaskom na taj tip odabir briše — inače bi ostao skriven u
    stanju i otišao na potpis.
  */
  useEffect(() => {
    if (formValues.tenantTypeId !== "P") return;

    setSubscriptionValues(initialSubscriptionStepForm);
  }, [formValues.tenantTypeId]);

  const fetchTenant = async (id: string) => {
    setIsFetching(true);
    try {
      const [result, subscription] = await Promise.all([
        getTenantAPI(id),
        getTenantSubscriptionAPI(id),
      ]);
      setLoadedTenant(result);
      setExistingSubscription(subscription);
      setFormValues({
        id: result.id,
        tenantTypeId: result.tenantType?.id ?? "C",
        companyName: result.companyName ?? null,
        firstName: result.firstName ?? null,
        lastName: result.lastName ?? null,
        headquartersAddress: result.headquartersAddress ?? null,
        address: result.address ?? null,
        phoneNumber: result.phoneNumber ?? null,
        mobilePhoneNumber: result.mobilePhoneNumber ?? null,
        email: result.email ?? null,
        web: result.web ?? null,
        personalIdentificationNumber: result.personalIdentificationNumber ?? null,
        timezone: result.timezone ?? null,
        logo: [],
      });
    } catch {
      // error handled by snackbar
    }
    setIsFetching(false);
  };

  const saveTenant = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      if (!formValues.id) {
        const result = await createTenantAPI(formValues);
        if (result) {
          setFormValues((prev) => ({ ...prev, id: result.id }));
        }
      } else {
        const editForm: TenantEditFormValues = {
          id: formValues.id,
          tenantTypeId: formValues.tenantTypeId,
          companyName: formValues.companyName,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          headquartersAddress: formValues.headquartersAddress,
          address: formValues.address,
          phoneNumber: formValues.phoneNumber,
          mobilePhoneNumber: formValues.mobilePhoneNumber,
          email: formValues.email,
          web: formValues.web,
          personalIdentificationNumber: formValues.personalIdentificationNumber,
          transactionAccountNumber: loadedTenant?.transactionAccountNumber ?? null,
          timezone: formValues.timezone,
          logo: [],
        };
        // Wizard ne uređuje logo, pa čuvamo postojeći upload da se ne obriše.
        const logoUploads: FleetDocumentUpload[] = loadedTenant?.logoUpload
          ? [loadedTenant.logoUpload]
          : [];
        await updateTenantAPI(formValues.id, editForm, logoUploads);
      }
      setIsLoading(false);
      return true;
    } catch (e: any) {
      setIsLoading(false);
      const oibError = getApiErrorByCode(e, "TenantOibExists");
      if (oibError) {
        setErrors((prev) => ({ ...prev, personalIdentificationNumber: oibError }));
      }
      return false;
    }
  };

  const handleNextStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      if (stepCurrent === 0) {
        const isValid = validateYup(
          formValues,
          TENANT_INFO_FORM_SCHEMA,
          setErrors
        );
        if (!isValid) return;
        const saved = await saveTenant();
        if (!saved) return;
      }

      if (stepCurrent === 1 && officeCount === 0) {
        showNotification("Morate dodati barem jednu poslovnicu.", { variant: "error" });
        return;
      }

      if (stepCurrent === 2 && userCount === 0) {
        showNotification("Morate dodati barem jednog korisnika.", { variant: "error" });
        return;
      }

      setStepCurrent((s) => s + 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleBackStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      setStepCurrent((s) => s - 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleFinish = async () => {
    const id = formValues.id;
    if (!id || !onSuccess) return;

    if (officeCount === 0) {
      showNotification("Morate dodati barem jednu poslovnicu.", { variant: "error" });
      setStepCurrent(1);
      return;
    }

    if (userCount === 0) {
      showNotification("Morate dodati barem jednog korisnika.", { variant: "error" });
      setStepCurrent(2);
      return;
    }

    /*
      Pretplata se potpisuje tek ovdje, u jednom pozivu s pregazivanjima. Fizička osoba je
      nema, a klijent koji je već ima ne dobiva drugu — to backend ionako odbija.
    */
    const needsSubscription =
      formValues.tenantTypeId !== "P" && existingSubscription === null;

    if (needsSubscription) {
      setIsLoading(true);

      try {
        await signTenantSubscriptionAPI(id, subscriptionValues);
      } catch {
        // poruka ide kroz snackbar; tenant je spremljen, pa se vraćamo na korak pretplate
        setIsLoading(false);
        setStepCurrent(3);
        return;
      }

      setIsLoading(false);
    }

    setFormValues(initialTenantCreateForm);
    setSubscriptionValues(initialSubscriptionStepForm);
    setStepCurrent(0);
    onSuccess(id);
  };

  const stepLabels = [
    "Osnovni podaci",
    "Poslovnice",
    "Korisnici",
    "Pretplata",
    "Pregled",
  ];

  const handleStepClick = (stepIndex: number) => {
    setStepCurrent(stepIndex);
  };

  const firstStepCompleted = !!formValues.id;

  const statusBar = (
    <TenantStepStatusBar
      stepLabels={stepLabels}
      stepCurrent={stepCurrent}
      onStepClick={handleStepClick}
      firstStepCompleted={firstStepCompleted}
    />
  );

  const steps = [
    <WizardStep key="info" stepNumber={0} stepCurrent={stepCurrent}>
      {statusBar}
      <TenantInfoStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        setErrors={setErrors}
      />
    </WizardStep>,
    <WizardStep key="offices" stepNumber={1} stepCurrent={stepCurrent}>
      {statusBar}
      <TenantOfficeStep
        formValues={formValues}
        onCountChange={handleOfficeCountChange}
      />
    </WizardStep>,
    <WizardStep key="users" stepNumber={2} stepCurrent={stepCurrent}>
      {statusBar}
      <TenantUserStep
        formValues={formValues}
        onCountChange={handleUserCountChange}
      />
    </WizardStep>,
    <WizardStep key="subscription" stepNumber={3} stepCurrent={stepCurrent}>
      {statusBar}
      <TenantSubscriptionStep
        tenantValues={formValues}
        formValues={subscriptionValues}
        setFormValues={setSubscriptionValues}
        existingSubscription={existingSubscription}
      />
    </WizardStep>,
    <WizardStep key="summary" stepNumber={4} stepCurrent={stepCurrent}>
      {statusBar}
      <TenantSummaryStep
        formValues={formValues}
        subscriptionValues={subscriptionValues}
        existingSubscription={existingSubscription}
      />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={isFullScreen ? "full" : "medium"}
      position={isFullScreen ? "side" : "centered"}
      intro={
        isFullScreen && showIntro
          ? {
              content: (
                <ModalIntro
                  animation={invoiceAnimation}
                  title={
                    isEdit
                      ? "Ažurirajte podatke tenanta!"
                      : "Kreirajte novog tenanta u tren!"
                  }
                  description={
                    isEdit
                      ? "Uredite podatke o tenantu, korisnicima i poslovnicama. Promjene su odmah dostupne u sustavu."
                      : "Unesite podatke o tenantu. Nakon unosa, tenant je odmah dostupan za korištenje u sustavu."
                  }
                  show={showIntro}
                />
              ),
            }
          : undefined
      }
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={title}
        onHide={onClose}
      />
      <ModalBody>
        {isFetching ? (
          <div className="relative min-h-[60vh] xl:min-h-[600px] h-[calc(100vh-160px)]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        ) : (
          <Wizard steps={steps} />
        )}
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(TenantEditCard);
